import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { MutateOptions } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  notesMutationKeys,
  type CreateNoteVars,
  type UpdateNoteVars,
  type DeleteNoteVars,
} from '../lib/offline/mutationDefaults';
import NotesV2ApiService, { 
  NotesV2Filters, 
  CreateNoteV2Data,
  UpdateNoteV2Data,
  NoteV2,
} from '../services/notesV2Api';

/**
 * Hook for managing workspace notes with v2 API
 */
export const useWorkspaceNotesV2 = (
  workspaceId: string, 
  filters: NotesV2Filters = {}, 
  page: number = 1,
  limit: number = 50
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['workspace-notes-v2', workspaceId, filters, page, limit],
    queryFn: () => NotesV2ApiService.getWorkspaceNotes(workspaceId, filters, page, limit),
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Filter/search changes make a new query key; keep showing the previous
    // page while it loads so the tab doesn't unmount (losing input focus).
    keepPreviousData: true,
  });

  // Create / update / delete reference the offline-capable defaults registered
  // in lib/offline/mutationDefaults.ts (mutationFn, optimistic update, rollback,
  // invalidation all live there) so a write made offline is queued and replayed
  // on reconnect — even across an app restart.
  const createNoteMutation = useMutation<NoteV2, unknown, CreateNoteVars>({
    mutationKey: notesMutationKeys.create,
  });

  const updateNoteMutation = useMutation<NoteV2, unknown, UpdateNoteVars>({
    mutationKey: notesMutationKeys.update,
  });

  const deleteNoteMutation = useMutation<void, unknown, DeleteNoteVars>({
    mutationKey: notesMutationKeys.delete,
  });

  return {
    notes: data?.notes || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetch,
    // Wrappers keep the original call signatures while injecting `workspaceId`
    // into the variables the registered defaults need to replay the write.
    createNote: (
      noteData: CreateNoteV2Data,
      options?: MutateOptions<NoteV2, unknown, CreateNoteVars>,
    ) => createNoteMutation.mutate({ workspaceId, noteData }, options),
    isCreating: createNoteMutation.isLoading,
    updateNote: (
      vars: { noteId: string; noteData: UpdateNoteV2Data },
      options?: MutateOptions<NoteV2, unknown, UpdateNoteVars>,
    ) => updateNoteMutation.mutate({ workspaceId, ...vars }, options),
    isUpdating: updateNoteMutation.isLoading,
    deleteNote: (
      noteId: string,
      options?: MutateOptions<void, unknown, DeleteNoteVars>,
    ) => deleteNoteMutation.mutate({ workspaceId, noteId }, options),
    isDeleting: deleteNoteMutation.isLoading
  };
};

/**
 * Hook for getting available entity types
 */
export const useEntityTypes = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['entity-types-v2'],
    queryFn: () => NotesV2ApiService.getEntityTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes (entity types don't change often)
  });

  return {
    entityTypes: data || [],
    isLoading,
    error
  };
};

/**
 * The workspace's tag vocabulary, for suggesting existing tags instead of
 * making people remember and retype them.
 *
 * Invalidated by note writes, since creating or editing a note is what changes
 * the tag set — a tag appears the moment the first note using it is saved, and
 * disappears when the last one stops using it.
 */
export const useWorkspaceNoteTags = (workspaceId: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['workspace-note-tags', workspaceId],
    queryFn: () => NotesV2ApiService.getWorkspaceNoteTags(workspaceId),
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    tags: data || [],
    isLoading,
    error,
  };
};

/**
 * Hook for getting notes for a specific entity
 * @param enabled - Optional flag to control when the query runs (useful for lazy loading)
 */
export const useEntityNotes = (
  entityType: string,
  entityId: string,
  workspaceId?: string,
  limit: number = 10,
  enabled: boolean = true
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['entity-notes-v2', entityType, entityId, workspaceId, limit],
    queryFn: () => NotesV2ApiService.getEntityNotes(entityType, entityId, workspaceId, limit),
    enabled: enabled && !!entityType && !!entityId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    notes: data?.notes || [],
    entityType: data?.entity_type,
    entityId: data?.entity_id,
    isLoading,
    error,
    refetch
  };
};

/**
 * Note counts for a whole table of entities in one request (#1179). Pass the
 * result down to row badges as `batchCount` so they never fetch on their own.
 * `'loading'` lets a badge show its pending state without owning a query.
 */
export type NoteCountsResult = Record<string, number> | 'loading';

export const useEntityNoteCounts = (
  entityType: string,
  entityIds: Array<string | number>,
  workspaceId?: string,
  enabled: boolean = true
): NoteCountsResult | undefined => {
  const idKey = entityIds.map((id) => String(id)).filter(Boolean).join(',');
  // A fresh array with the same ids must not refetch, so key on the joined string.
  const ids = useMemo(() => (idKey ? [...new Set(idKey.split(','))].sort() : []), [idKey]);
  const active = enabled && !!workspaceId && !!entityType && ids.length > 0;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['entity-note-counts', entityType, workspaceId, ids],
    queryFn: () => NotesV2ApiService.getEntityNoteCounts(workspaceId as string, entityType, ids),
    enabled: active,
    staleTime: 2 * 60 * 1000,
  });
  // A failed batch must not read as "still loading" forever — that disables
  // every badge. Fall back to per-row counts instead.
  if (!active || (isError && !data)) return undefined;
  if (isLoading || !data) return 'loading';
  return data;
};

/** One row's count from a batch result; undefined means "no batch in use, fetch yourself". */
export const noteCountFor = (
  counts: NoteCountsResult | undefined,
  entityId: string | number
): number | 'loading' | undefined => {
  if (counts === undefined) return undefined;
  if (counts === 'loading') return 'loading';
  return counts[String(entityId)] ?? 0;
};

/**
 * Hook for getting note count for an entity (badge display)
 */
export const useEntityNoteCount = (
  entityType: string,
  entityId: string,
  workspaceId?: string
) => {
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!entityType || !entityId) return;

    const fetchCount = async () => {
      setIsLoading(true);
      try {
        const noteCount = await NotesV2ApiService.getEntityNoteCount(
          entityType, 
          entityId, 
          workspaceId
        );
        setCount(noteCount);
      } catch (error) {
        console.error('Error fetching note count:', error);
        setCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCount();
  }, [entityType, entityId, workspaceId]);

  return { count, isLoading };
};

/**
 * Hook for bulk note creation (both options: one note for multiple entities, or separate notes)
 */
export const useBulkNoteCreation = (workspaceId: string) => {
  const queryClient = useQueryClient();

  const createSingleNoteForMultipleEntities = useMutation({
    mutationFn: async ({ 
      noteData, 
      entities 
    }: { 
      noteData: Omit<CreateNoteV2Data, 'entities'>, 
      entities: { entity_type: string; entity_id: string; entity_display_name?: string }[] 
    }) => {
      return NotesV2ApiService.createNote(workspaceId, {
        ...noteData,
        entities
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workspace-notes-v2', workspaceId]);
      queryClient.invalidateQueries(['entity-notes-v2']);
      queryClient.invalidateQueries(['entity-note-counts']);
      toast.success('Note created for all selected entities');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create note');
    }
  });

  const createSeparateNotesForEntities = useMutation({
    mutationFn: async ({ 
      noteData, 
      entities 
    }: { 
      noteData: Omit<CreateNoteV2Data, 'entities'>, 
      entities: { entity_type: string; entity_id: string; entity_display_name?: string }[] 
    }) => {
      // Create separate notes for each entity
      const promises = entities.map(entity => 
        NotesV2ApiService.createNote(workspaceId, {
          ...noteData,
          title: noteData.title ? 
            `${noteData.title} - ${entity.entity_display_name}` : 
            entity.entity_display_name,
          entities: [entity]
        })
      );
      
      return Promise.all(promises);
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries(['workspace-notes-v2', workspaceId]);
      queryClient.invalidateQueries(['entity-notes-v2']);
      queryClient.invalidateQueries(['entity-note-counts']);
      toast.success(`Created ${results.length} separate notes`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create notes');
    }
  });

  return {
    createSingleNote: createSingleNoteForMultipleEntities.mutate,
    createSeparateNotes: createSeparateNotesForEntities.mutate,
    isCreatingSingle: createSingleNoteForMultipleEntities.isLoading,
    isCreatingSeparate: createSeparateNotesForEntities.isLoading
  };
};
